import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, X, Search } from "lucide-react";
import { useLanguage, translations } from "@/i18n/LanguageContext";
import { blogPosts } from "@/data/blogPosts";

interface SearchResult {
  label: string;
  description?: string;
  href: string;
  category: string;
}

// Team members for search (static data mirroring TeamPage)
const teamNames = [
  "Rune Kristoffersen", "Kolbjørn Akervold", "Rune Indrøy", "Nina Rebnord", "Fredrik Årvåg", "Kristin Aa",
  "Elisabeth Nesse", "Anne Husby Rosnes", "Jo Hellesund",
  "Artur Ferensowicz", "Marianne Heimark", "Trude Ingebrigtsen Bjørnestad",
  "Heino Skjold Danielsen", "Aurel Constantinica", "Franz R. Herbst",
  "Øystein Lauritsen", "Knut Helge Sandvik", "Tobias Utne", "Bjørn Silkoset",
  "Helge Herland",
];

const productNames = [
  "Pukk 16–32 mm", "Pukk 16–64 mm", "Pukk 32–64 mm", "Pukk 64–90 mm",
  "Singel 8–16 mm", "Singel 2–8 mm", "Kabelsand 0–2 mm",
  "Veigrus 0–8 mm", "Veigrus 0–16 mm", "Grus 0–32 mm",
  "Anleggsjord uten sand", "Anleggsjord med sand", "Big Bag",
];

const SearchCommand = ({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { t } = useLanguage();
  const bm = translations.burgerMenu;

  const categoryLabels = useMemo(() => ({
    page: t({ no: "Side", en: "Page", de: "Seite" }),
    article: t({ no: "Artikkel", en: "Article", de: "Artikel" }),
    person: t({ no: "Person", en: "Person", de: "Person" }),
    product: t({ no: "Produkt", en: "Product", de: "Produkt" }),
  }), [t]);

  const allResults: SearchResult[] = useMemo(() => {
    const pages: SearchResult[] = [
      { label: t(bm.deliveryOfMaterials), description: t({ no: "Mottak av forurensede masser", en: "Reception of contaminated materials", de: "Annahme kontaminierter Materialien" }), href: "/mottak", category: categoryLabels.page },
      { label: t(bm.saleOfRecycled), description: t({ no: "Pukk, singel, grus, jord, kabelsand", en: "Crushed stone, gravel, soil, cable sand", de: "Schotter, Kies, Erde, Kabelsand" }), href: "/gjenvunne-masser", category: categoryLabels.page },
      { label: t(bm.soilWashingPlant), description: t({ no: "Vasking og rensing av forurenset jord", en: "Washing and cleaning of contaminated soil", de: "Waschen und Reinigen von kontaminiertem Boden" }), href: "/jordvaskeanlegg", category: categoryLabels.page },
      { label: t(bm.steamingOfSoil), description: t({ no: "Damping for fjerning av uønskete arter", en: "Steaming for removal of unwanted species", de: "Dämpfung zur Entfernung unerwünschter Arten" }), href: "/jorddampeanlegg", category: categoryLabels.page },
      { label: t(bm.knowledgeCentre), description: t({ no: "Kunnskap om sirkulær massehåndtering", en: "Knowledge about circular mass management", de: "Wissen über zirkuläre Massenverwaltung" }), href: "/kompetansesenter", category: categoryLabels.page },
      { label: t(bm.laboratory), description: t({ no: "Analyse og testing av masser", en: "Analysis and testing of materials", de: "Analyse und Prüfung von Materialien" }), href: "/laboratorium", category: categoryLabels.page },
      { label: t(bm.ispsQuay), description: t({ no: "ISPS-godkjent kai for sjøtransport", en: "ISPS-approved quay for sea transport", de: "ISPS-zugelassener Kai für Seetransport" }), href: "/isps-kai", category: categoryLabels.page },
      { label: t(bm.aboutUs), description: "Envir", href: "/om-oss", category: categoryLabels.page },
      { label: t(bm.ourSustainability), description: t({ no: "Miljø, klima og bærekraft", en: "Environment, climate and sustainability", de: "Umwelt, Klima und Nachhaltigkeit" }), href: "/baerekraft", category: categoryLabels.page },
      { label: t(bm.meetThePeople), description: t({ no: "Menneskene bak Envir", en: "The people behind Envir", de: "Die Menschen hinter Envir" }), href: "/team", category: categoryLabels.page },
      { label: t(bm.salesTerms), description: t({ no: "Betingelser for salg og mottak", en: "Terms for sales and reception", de: "Verkaufs- und Empfangsbedingungen" }), href: "/salgsbetingelser", category: categoryLabels.page },
      { label: t(bm.newsAndInsight), description: t({ no: "Blogg, nyheter og innsikt", en: "Blog, news and insight", de: "Blog, Neuigkeiten und Einblicke" }), href: "/nyheter", category: categoryLabels.page },
      { label: t(translations.nav.contact), description: t({ no: "Ta kontakt med oss", en: "Get in touch with us", de: "Kontaktieren Sie uns" }), href: "/kontakt", category: categoryLabels.page },
    ];

    const articles: SearchResult[] = blogPosts.map((post) => ({
      label: post.title,
      description: post.excerpt,
      href: `/blogg/${post.slug}`,
      category: categoryLabels.article,
    }));

    const people: SearchResult[] = teamNames.map((name) => ({
      label: name,
      href: "/team",
      category: categoryLabels.person,
    }));

    const products: SearchResult[] = productNames.map((name) => ({
      label: name,
      href: "/gjenvunne-masser",
      category: categoryLabels.product,
    }));

    return [...pages, ...articles, ...people, ...products];
  }, [t, categoryLabels]);

  const filtered = useMemo(() => {
    if (!query.trim()) return allResults.slice(0, 8);
    const q = query.toLowerCase();
    // Search across label + description
    return allResults.filter(
      (r) =>
        r.label.toLowerCase().includes(q) ||
        (r.description && r.description.toLowerCase().includes(q))
    );
  }, [query, allResults]);

  const handleSelect = useCallback(
    (href: string) => {
      onOpenChange(false);
      setQuery("");
      navigate(href);
    },
    [navigate, onOpenChange]
  );

  // Focus input on open
  useEffect(() => {
    if (open) {
      setQuery("");
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  // ⌘K / Ctrl+K + Escape
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        onOpenChange(!open);
      }
      if (e.key === "Escape" && open) {
        onOpenChange(false);
      }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, [open, onOpenChange]);

  // Lock body scroll
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  // Category color mapping
  const categoryColor: Record<string, string> = {
    [categoryLabels.page]: "text-primary",
    [categoryLabels.article]: "text-emerald-400",
    [categoryLabels.person]: "text-sky-400",
    [categoryLabels.product]: "text-amber-400",
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[70] bg-envir-charcoal/95 backdrop-blur-xl flex flex-col"
          onClick={() => onOpenChange(false)}
        >
          {/* Close button */}
          <div className="absolute top-6 right-6 md:right-10">
            <button
              onClick={() => onOpenChange(false)}
              className="w-10 h-10 rounded-full border border-primary-foreground/20 flex items-center justify-center hover:bg-primary-foreground/10 transition-colors"
              aria-label="Close search"
            >
              <X className="w-5 h-5 text-primary-foreground" />
            </button>
          </div>

          {/* Search area */}
          <div
            className="flex-1 flex flex-col items-center justify-start pt-[18vh] px-6"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Large search input */}
            <div className="w-full max-w-2xl relative">
              <Search className="absolute left-0 top-1/2 -translate-y-1/2 w-6 h-6 text-primary-foreground/30" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t({
                  no: "Søk etter sider, produkter, personer...",
                  en: "Search pages, products, people...",
                  de: "Seiten, Produkte, Personen suchen...",
                })}
                className="w-full bg-transparent border-none outline-none text-2xl md:text-4xl font-display font-400 text-primary-foreground placeholder:text-primary-foreground/20 pl-10 pb-4 border-b-2 border-primary-foreground/10 focus:border-primary transition-colors"
                style={{ borderBottomWidth: "1px", borderBottomStyle: "solid" }}
              />
              <p className="mt-3 text-xs font-body text-primary-foreground/30">
                {t({
                  no: "Trykk Enter eller klikk for å navigere",
                  en: "Press Enter or click to navigate",
                  de: "Drücken Sie Enter oder klicken Sie zum Navigieren",
                })}
                <span className="ml-2 hidden md:inline opacity-50">⌘K</span>
              </p>
            </div>

            {/* Results */}
            <div className="w-full max-w-2xl mt-8 space-y-1 max-h-[45vh] overflow-y-auto">
              {filtered.length === 0 ? (
                <p className="text-sm font-body text-primary-foreground/40 py-4">
                  {t({
                    no: "Ingen resultater funnet.",
                    en: "No results found.",
                    de: "Keine Ergebnisse gefunden.",
                  })}
                </p>
              ) : (
                filtered.map((result, i) => (
                  <motion.button
                    key={`${result.href}-${result.label}`}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2, delay: i * 0.02 }}
                    onClick={() => handleSelect(result.href)}
                    className="group w-full flex items-center justify-between py-3 px-3 rounded-lg hover:bg-primary-foreground/5 transition-colors text-left"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className={`text-[10px] font-body font-500 uppercase tracking-widest shrink-0 w-16 ${categoryColor[result.category] || "text-primary"}`}>
                        {result.category}
                      </span>
                      <div className="min-w-0">
                        <span className="text-sm md:text-base font-body text-primary-foreground/70 group-hover:text-primary-foreground transition-colors block truncate">
                          {result.label}
                        </span>
                        {result.description && query.trim() && (
                          <span className="text-xs font-body text-primary-foreground/30 block truncate mt-0.5">
                            {result.description}
                          </span>
                        )}
                      </div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-primary-foreground/20 group-hover:text-primary shrink-0 transition-all group-hover:translate-x-0.5" />
                  </motion.button>
                ))
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SearchCommand;
